import React, { useEffect, useRef, useState } from "react";
import { BookOpen, Pencil, Plus, Trash2, Upload, Loader2, FileCheck2, FileX2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { useAuth } from "../../context/AuthContext";
import {
  fetchAllBooks, createBook, updateBook, deleteBook, uploadBookFile, fetchBookPurchases, money, formatDateTime,
  fetchBookBundle, updateBookBundle,
} from "../../lib/api";

const empty = { title: "", author: "Phil Dickens", description: "", price: "19.99", cover_url: "", pages: "", isbn: "" };

export default function Books() {
  const { token } = useAuth();
  const [books, setBooks] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploadingId, setUploadingId] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [bundlePrice, setBundlePrice] = useState("");
  const fileRef = useRef(null);
  const uploadTargetRef = useRef(null);

  const load = () => {
    fetchAllBooks(token).then(setBooks).catch(() => {});
    fetchBookPurchases(token).then(setPurchases).catch(() => {});
    fetchBookBundle().then((b) => { setBundle(b); setBundlePrice(String(b.price ?? "")); }).catch(() => {});
  };
  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);

  const openCreate = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (b) => {
    setEditingId(b.id);
    setForm({
      title: b.title || "", author: b.author || "", description: b.description || "",
      price: String(b.price ?? ""), cover_url: b.cover_url || "", pages: b.pages ? String(b.pages) : "", isbn: b.isbn || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    const price = parseFloat(form.price);
    if (isNaN(price) || price <= 0) return toast.error("Enter a valid price");
    const data = {
      title: form.title.trim(), author: form.author.trim(), description: form.description.trim(),
      price, cover_url: form.cover_url.trim() || null,
      pages: form.pages ? parseInt(form.pages, 10) : null, isbn: form.isbn.trim() || null,
    };
    try {
      if (editingId) await updateBook(token, editingId, data);
      else await createBook(token, data);
      toast.success(editingId ? "Book updated" : "Book added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the book");
    }
  };

  const toggleActive = async (b) => {
    try {
      await updateBook(token, b.id, { active: !b.active });
      load();
    } catch (e) {
      toast.error("Could not update visibility");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    try {
      await deleteBook(token, b.id);
      toast.success("Book deleted");
      load();
    } catch (e) {
      toast.error("Could not delete the book");
    }
  };

  const pickFile = (b) => { uploadTargetRef.current = b.id; fileRef.current?.click(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const bookId = uploadTargetRef.current;
    if (!file || !bookId) return;
    setUploadingId(bookId);
    try {
      await uploadBookFile(token, bookId, file);
      toast.success("PDF uploaded — buyers now get the real book");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  const saveBundle = async (data) => {
    try {
      const fresh = await updateBookBundle(token, data);
      setBundle((b) => ({ ...b, ...fresh }));
      toast.success("Bundle updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the bundle");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-bold text-navy">Books</h1>
          <p className="text-gray-500 text-sm mt-0.5">Digital downloads for sale — edit prices, covers & PDFs anytime.</p>
        </div>
        <Button onClick={openCreate} className="bg-navy hover:bg-navy-light text-white" data-testid="add-book-button">
          <Plus className="h-4 w-4 mr-1.5" /> Add Book
        </Button>
      </div>

      <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={onFile} data-testid="book-file-input" />

      {bundle && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-testid="crm-bundle-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-navy">WOW Series Bundle</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                All active books ({bundle.book_count}) in one purchase — buyers download a ZIP.
                Regular total {money(bundle.regular_total)}.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <Input
                  type="number" step="0.01" value={bundlePrice}
                  onChange={(e) => setBundlePrice(e.target.value)}
                  className="w-28 pl-7 h-9"
                  data-testid="bundle-price-input"
                />
              </div>
              <Button
                size="sm"
                onClick={() => {
                  const p = parseFloat(bundlePrice);
                  if (isNaN(p) || p <= 0) return toast.error("Enter a valid price");
                  saveBundle({ price: p });
                }}
                className="bg-navy hover:bg-navy-light text-white"
                data-testid="bundle-save-price"
              >
                Save Price
              </Button>
              <button
                onClick={() => saveBundle({ active: !bundle.active })}
                className={`text-xs px-3 py-1.5 rounded-full ${bundle.active ? "bg-navy/10 text-navy" : "bg-gray-100 text-gray-500"}`}
                data-testid="bundle-active-toggle"
              >
                {bundle.active ? "Enabled" : "Disabled"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        {books.map((b) => (
          <div key={b.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex gap-4" data-testid={`crm-book-${b.id}`}>
            {b.cover_url ? (
              <img src={b.cover_url} alt={b.title} className="h-36 w-24 rounded-md object-cover shadow flex-shrink-0" />
            ) : (
              <div className="h-36 w-24 rounded-md bg-cream flex items-center justify-center flex-shrink-0">
                <BookOpen className="h-8 w-8 text-gray-300" />
              </div>
            )}
            <div className="min-w-0 flex-1 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-navy leading-snug">{b.title}</h3>
                <span className="font-serif text-lg font-bold text-navy whitespace-nowrap">{money(b.price)}</span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">{b.author}</div>
              <div className="flex items-center gap-2 mt-2 text-xs">
                {b.has_file ? (
                  <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded-full"><FileCheck2 className="h-3.5 w-3.5" /> PDF uploaded</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full"><FileX2 className="h-3.5 w-3.5" /> Placeholder PDF</span>
                )}
                <button
                  onClick={() => toggleActive(b)}
                  className={`px-2 py-0.5 rounded-full ${b.active ? "bg-navy/10 text-navy" : "bg-gray-100 text-gray-500"}`}
                  data-testid={`book-active-toggle-${b.id}`}
                >
                  {b.active ? "Visible" : "Hidden"}
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-auto pt-3">
                <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`edit-book-${b.id}`}>
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => pickFile(b)} disabled={uploadingId === b.id} data-testid={`upload-book-pdf-${b.id}`}>
                  {uploadingId === b.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                  {b.has_file ? "Replace PDF" : "Upload PDF"}
                </Button>
                <button onClick={() => remove(b)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500" data-testid={`delete-book-${b.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-100 font-semibold text-navy">Purchases ({purchases.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream text-gray-500">
              <tr>
                <th className="text-left px-5 py-2.5 font-medium">Date</th>
                <th className="text-left px-5 py-2.5 font-medium">Book</th>
                <th className="text-left px-5 py-2.5 font-medium">Buyer</th>
                <th className="text-right px-5 py-2.5 font-medium">Amount</th>
                <th className="text-right px-5 py-2.5 font-medium">Downloads</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p) => (
                <tr key={p.id} className="border-t border-gray-50">
                  <td className="px-5 py-2.5 text-gray-500 whitespace-nowrap">{formatDateTime(p.paid_at)}</td>
                  <td className="px-5 py-2.5 text-navy">{p.book_title}</td>
                  <td className="px-5 py-2.5 text-gray-600">
                    <div>{p.payer_name || "—"}</div>
                    <div className="text-xs text-gray-400">{p.payer_email}</div>
                  </td>
                  <td className="px-5 py-2.5 text-right font-medium text-navy">{money(p.amount)}</td>
                  <td className="px-5 py-2.5 text-right text-gray-600">{p.downloads || 0}</td>
                </tr>
              ))}
              {purchases.length === 0 && (
                <tr><td colSpan="5" className="px-5 py-10 text-center text-gray-400">No purchases yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="book-dialog">
          <DialogHeader><DialogTitle className="font-serif text-navy">{editingId ? "Edit Book" : "Add Book"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Input placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="book-title-input" />
            <Input placeholder="Author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="book-author-input" />
            <Textarea placeholder="Description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="book-description-input" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">Price (USD)</label>
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="book-price-input" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Pages (optional)</label>
                <Input type="number" value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} />
              </div>
            </div>
            <Input placeholder="ISBN (optional)" value={form.isbn} onChange={(e) => setForm({ ...form, isbn: e.target.value })} />
            <Input placeholder="Cover image URL" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-navy hover:bg-navy-light text-white" data-testid="book-save-button">
              {editingId ? "Save Changes" : "Add Book"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
